import { useState, useEffect, useMemo, useCallback } from 'react';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    RefreshCw,
    Search,
    Filter,
    Link as LinkIcon,
    Unlink,
    AlertTriangle,
    CheckCircle2,
    HelpCircle,
    FileText,
    Loader2,
    DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supplierReconciliationApi } from '../lib/api';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'matched', label: 'Matched' },
    { value: 'variance', label: 'Variance' },
    { value: 'variance_review', label: 'Needs review' },
    { value: 'currency_mismatch', label: 'Currency mismatch' },
    { value: 'suggested', label: 'Suggested' },
    { value: 'unmatched', label: 'Unmatched PO' },
    { value: 'unmatched_invoice', label: 'Orphan invoice' },
];

const STATUS_STYLES = {
    matched: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    variance: 'bg-amber-50 text-amber-700 border-amber-200',
    variance_review: 'bg-orange-50 text-orange-700 border-orange-200',
    currency_mismatch: 'bg-violet-50 text-violet-700 border-violet-200',
    suggested: 'bg-blue-50 text-blue-700 border-blue-200',
    unmatched: 'bg-slate-50 text-slate-600 border-slate-200',
    unmatched_invoice: 'bg-slate-50 text-slate-600 border-slate-200',
};

const STATUS_LABELS = {
    matched: 'Matched',
    variance: 'Variance',
    variance_review: 'Needs review',
    currency_mismatch: 'Currency mismatch',
    suggested: 'Suggested',
    unmatched: 'Unmatched PO',
    unmatched_invoice: 'Orphan invoice',
};

function formatMoney(amount, currency) {
    if (amount === null || amount === undefined) return '—';
    const formatted = new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted} ${currency || ''}`.trim();
}

function StatusBadge({ status }) {
    const cls = STATUS_STYLES[status] || 'bg-slate-50 text-slate-600 border-slate-200';
    return (
        <Badge
            variant="outline"
            className={`${cls} font-medium`}
            data-testid={`row-status-${status}`}
        >
            {STATUS_LABELS[status] || status}
        </Badge>
    );
}

function ReconciliationRow({ row, onMatch, onUnmatch, onExplain }) {
    const p = row.purchase || {};
    const inv = row.invoice || {};
    const order = row.order || {};
    const isOrphan = row.row_kind === 'invoice_orphan';
    const isMatched = ['matched', 'variance', 'variance_review', 'currency_mismatch'].includes(row.status);

    return (
        <tr className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid="reconciliation-row">
            {/* PO */}
            <td className="py-3 px-3 align-top">
                {isOrphan ? (
                    <span className="text-slate-400 italic">No PO</span>
                ) : (
                    <div>
                        <div className="font-mono text-xs text-slate-900">{p.po_number || p.supplier_reference || p.id?.slice(0, 8)}</div>
                        <div className="text-xs text-slate-500">{p.vendor}</div>
                    </div>
                )}
            </td>
            {/* Catalogue / Order */}
            <td className="py-3 px-3 align-top">
                {order.id ? (
                    <div>
                        <div className="font-mono text-xs text-slate-900">{order.catalogue_number || '—'}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[200px]" title={order.title}>
                            {order.artist ? `${order.artist} · ` : ''}{order.title}
                        </div>
                        <div className="text-xs text-slate-400">#{order.order_number || order.id?.slice(0, 6)}</div>
                    </div>
                ) : (
                    <span className="text-slate-400">—</span>
                )}
            </td>
            {/* Supplier */}
            <td className="py-3 px-3 align-top">
                {inv.supplier_name ? (
                    <div>
                        <div className="text-sm text-slate-900">{inv.supplier_name}</div>
                        <div className="font-mono text-xs text-slate-500">{inv.supplier_invoice_number || `V${inv.voucher_number}`}</div>
                        {inv.invoice_date && (
                            <div className="text-xs text-slate-400">{inv.invoice_date}</div>
                        )}
                        {inv.parsed_catalogue_number && (
                            <div
                                className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                                title="Catalogue number parsed from e-conomic 'text' field after the first comma"
                                data-testid="row-parsed-cat"
                            >
                                Cat# {inv.parsed_catalogue_number}
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="text-slate-400">—</span>
                )}
            </td>
            {/* Expected */}
            <td className="py-3 px-3 align-top text-right font-mono text-sm text-slate-700">
                {formatMoney(row.expected_amount, row.expected_currency)}
            </td>
            {/* Actual */}
            <td className="py-3 px-3 align-top text-right font-mono text-sm text-slate-900 font-medium">
                {formatMoney(row.actual_amount, row.actual_currency)}
            </td>
            {/* Difference */}
            <td className="py-3 px-3 align-top text-right font-mono text-sm">
                {row.difference !== null && row.difference !== undefined ? (
                    <div className={row.difference === 0 ? 'text-slate-500' : Math.abs(row.difference) > 5 ? 'text-amber-700' : 'text-slate-700'}>
                        {row.difference > 0 ? '+' : ''}{row.difference.toFixed(2)}
                        {row.difference_pct !== null && row.difference_pct !== undefined && (
                            <div className="text-xs text-slate-400">{row.difference_pct > 0 ? '+' : ''}{row.difference_pct.toFixed(1)}%</div>
                        )}
                    </div>
                ) : (
                    <span className="text-slate-300">—</span>
                )}
            </td>
            {/* Status */}
            <td className="py-3 px-3 align-top">
                <div className="flex flex-col gap-1.5">
                    <StatusBadge status={row.status} />
                    {row.confidence && (
                        <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200 w-fit">
                            {row.confidence}
                        </Badge>
                    )}
                    {row.match_reasons && row.match_reasons.length > 0 && (
                        <div className="text-xs text-slate-500 max-w-[200px]" title={row.match_reasons.join(' · ')}>
                            {row.match_reasons.slice(0, 2).join(' · ')}
                            {row.match_reasons.length > 2 ? ' · …' : ''}
                        </div>
                    )}
                </div>
            </td>
            {/* Actions */}
            <td className="py-3 px-3 align-top">
                <div className="flex flex-col gap-1">
                    {isMatched && row.match ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onUnmatch(row.match.id)}
                            data-testid="row-unmatch-btn"
                        >
                            <Unlink className="h-3.5 w-3.5 mr-1" />
                            Unmatch
                        </Button>
                    ) : !isOrphan && p.id ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onMatch(row)}
                            data-testid="row-match-btn"
                        >
                            <LinkIcon className="h-3.5 w-3.5 mr-1" />
                            Match
                        </Button>
                    ) : (
                        <span className="text-xs text-slate-400">—</span>
                    )}
                    {/* "Why no match?" button on orphan invoices */}
                    {isOrphan && inv.id && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-slate-500 h-7 px-2"
                            onClick={() => onExplain(inv)}
                            data-testid="row-explain-btn"
                            title="See why this invoice didn't auto-match any PO"
                        >
                            <HelpCircle className="h-3.5 w-3.5 mr-1" />
                            Why no match?
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    );
}

function ExplainDialog({ invoice, open, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !invoice?.id) return;
        setLoading(true);
        supplierReconciliationApi.explain(invoice.id)
            .then(({ data: d }) => setData(d))
            .catch((err) => {
                toast.error(err.response?.data?.detail || 'Failed to explain');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [open, invoice?.id]);

    if (!invoice) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="explain-dialog">
                <DialogHeader>
                    <DialogTitle>Why didn't this invoice match?</DialogTitle>
                    <DialogDescription>
                        Live scoring breakdown — what the matcher saw vs every FLOW purchase.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                ) : data ? (
                    <div className="space-y-4">
                        {/* Invoice summary */}
                        <div className="bg-slate-50 rounded-lg p-3 text-sm">
                            <div className="font-medium text-slate-900">{data.invoice.supplier_name}</div>
                            <div className="font-mono text-xs text-slate-500">{data.invoice.supplier_invoice_number} · {data.invoice.invoice_date}</div>
                            <div className="font-mono text-sm text-slate-700 mt-1">{data.invoice.amount} {data.invoice.currency}</div>
                            {data.invoice.parsed_catalogue_number && (
                                <div className="mt-2 inline-block text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Cat# {data.invoice.parsed_catalogue_number}
                                </div>
                            )}
                            {data.invoice.text && (
                                <div className="text-xs text-slate-500 mt-2 italic">"{data.invoice.text}"</div>
                            )}
                        </div>

                        {/* Outcome */}
                        <div className={`rounded-lg p-3 text-sm font-medium ${
                            data.outcome.startsWith('would-auto') ? 'bg-emerald-50 text-emerald-800' :
                            data.outcome.startsWith('would-suggest') ? 'bg-blue-50 text-blue-800' :
                            'bg-amber-50 text-amber-800'
                        }`}>
                            Outcome: {data.outcome}
                            <div className="text-xs font-normal mt-1 opacity-80">
                                {data.total_purchases_checked} purchase(s) checked, {data.orders_loaded} order(s) loaded
                            </div>
                        </div>

                        {/* Top candidates */}
                        {data.top10_candidates && data.top10_candidates.length > 0 ? (
                            <div>
                                <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">
                                    Top {Math.min(10, data.top10_candidates.length)} candidates by score
                                </div>
                                <div className="space-y-2">
                                    {data.top10_candidates.map((c, idx) => (
                                        <div key={idx} className="border rounded-md p-2.5 text-sm bg-card">
                                            <div className="flex justify-between items-start gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-mono text-xs text-slate-900">
                                                        PO {c.po_number || c.po_id?.slice(0, 8)} · {c.po_vendor}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {c.po_amount} {c.po_currency} · planned {c.po_planned_date || '—'}
                                                    </div>
                                                    {c.order_catalogue_number && (
                                                        <div className="text-xs text-slate-500 mt-0.5">
                                                            Order: <span className="font-mono">{c.order_catalogue_number}</span>
                                                            {c.order_disc_sub_cats?.length > 0 && (
                                                                <> · discs: <span className="font-mono text-[10px]">{c.order_disc_sub_cats.filter(Boolean).join(', ') || '(none)'}</span></>
                                                            )}
                                                        </div>
                                                    )}
                                                    {c.reasons?.length > 0 ? (
                                                        <ul className="mt-1 space-y-0.5">
                                                            {c.reasons.map((r, i) => (
                                                                <li key={i} className="text-xs text-emerald-700">✓ {r}</li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <div className="text-xs text-slate-400 mt-1 italic">No signals matched</div>
                                                    )}
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <div className={`text-lg font-display font-semibold ${
                                                        c.score >= 80 ? 'text-emerald-700' :
                                                        c.score >= 50 ? 'text-blue-700' :
                                                        c.score >= 25 ? 'text-amber-700' : 'text-slate-400'
                                                    }`}>{c.score}</div>
                                                    <div className="text-[10px] uppercase text-slate-400">{c.bucket}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                                <strong>No FLOW purchases exist at all.</strong> The matcher has nothing to compare against. Create a purchase order linked to an order whose catalogue matches the invoice's parsed catalogue number.
                            </div>
                        )}

                        {/* Legend */}
                        <details className="text-xs text-slate-500">
                            <summary className="cursor-pointer">Scoring legend</summary>
                            <pre className="font-mono text-xs bg-slate-50 p-2 rounded mt-1">{JSON.stringify(data.scoring_legend, null, 2)}</pre>
                        </details>
                    </div>
                ) : null}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ManualMatchDialog({ row, open, onClose, onConfirm }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Pre-fill search with PO context
    useEffect(() => {
        if (!open) return;
        const p = row?.purchase || {};
        const order = row?.order || {};
        const initial = p.vendor || order.catalogue_number || '';
        setQuery(initial);
        setSelectedInvoice(null);
        setNote('');
    }, [open, row]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await supplierReconciliationApi.searchInvoices(query, true);
                setResults(data.items || []);
            } catch (err) {
                toast.error('Search failed');
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [query, open]);

    if (!row) return null;
    const p = row.purchase || {};
    const order = row.order || {};

    const handleSubmit = async () => {
        if (!selectedInvoice) {
            toast.error('Pick an invoice first');
            return;
        }
        setSubmitting(true);
        try {
            await supplierReconciliationApi.match(p.id, selectedInvoice.id, note);
            toast.success('Match created');
            onConfirm();
            onClose();
        } catch (err) {
            const msg = err.response?.data?.detail || 'Could not create match';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="manual-match-dialog">
                <DialogHeader>
                    <DialogTitle>Match invoice to PO</DialogTitle>
                    <DialogDescription>
                        Pick an unmatched supplier invoice to link to this FLOW purchase order.
                    </DialogDescription>
                </DialogHeader>

                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="text-xs text-slate-500">PO</div>
                            <div className="font-mono">{p.po_number || p.supplier_reference || p.id?.slice(0, 8)}</div>
                            <div className="text-slate-600">{p.vendor}</div>
                        </div>
                        <div>
                            <div className="text-xs text-slate-500">Expected</div>
                            <div className="font-mono font-medium">{formatMoney(row.expected_amount, row.expected_currency)}</div>
                            <div className="text-slate-600 text-xs truncate">{order.title} {order.catalogue_number ? `· ${order.catalogue_number}` : ''}</div>
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Search invoices</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Supplier name, invoice no., voucher no., text…"
                            className="pl-9"
                            data-testid="match-search-input"
                        />
                    </div>
                </div>

                <div className="border rounded-lg max-h-72 overflow-y-auto">
                    {loading ? (
                        <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                    ) : results.length === 0 ? (
                        <div className="p-6 text-center text-slate-400 text-sm">No invoices found</div>
                    ) : (
                        results.map((inv) => {
                            const selected = selectedInvoice?.id === inv.id;
                            const currencyMismatch = inv.currency && row.expected_currency && inv.currency !== row.expected_currency;
                            return (
                                <button
                                    key={inv.id}
                                    type="button"
                                    onClick={() => setSelectedInvoice(inv)}
                                    className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                                    data-testid="match-invoice-option"
                                >
                                    <div className="flex justify-between items-start gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-slate-900 truncate">{inv.supplier_name || `Supplier #${inv.supplier_id}`}</div>
                                            <div className="font-mono text-xs text-slate-500">
                                                {inv.supplier_invoice_number || `V${inv.voucher_number}`}
                                                {inv.invoice_date ? ` · ${inv.invoice_date}` : ''}
                                            </div>
                                            {inv.parsed_catalogue_number && (
                                                <div className="mt-0.5 inline-block text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    Cat# {inv.parsed_catalogue_number}
                                                </div>
                                            )}
                                            {inv.text && (
                                                <div className="text-xs text-slate-400 truncate mt-0.5">{inv.text}</div>
                                            )}
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-mono font-medium text-sm text-slate-900">{formatMoney(inv.amount, inv.currency)}</div>
                                            {currencyMismatch && (
                                                <div className="text-xs text-violet-700 mt-0.5">Currency differs from PO</div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {selectedInvoice && (
                    <div>
                        <label className="text-sm font-medium text-slate-700">Note (optional)</label>
                        <Input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Why this match?"
                            data-testid="match-note-input"
                        />
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!selectedInvoice || submitting}
                        data-testid="match-confirm-btn"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <LinkIcon className="h-4 w-4 mr-1" />}
                        Confirm match
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function SupplierReconciliationPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';

    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [emptyPosHidden, setEmptyPosHidden] = useState(0);
    const [tolerance, setTolerance] = useState({ flat: 5, pct: 0.02 });

    const [statusFilter, setStatusFilter] = useState('all');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [includeEmpty, setIncludeEmpty] = useState(false);

    const [matchRow, setMatchRow] = useState(null);
    const [explainInvoice, setExplainInvoice] = useState(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (supplierFilter) params.supplier = supplierFilter;
            if (searchFilter) params.search = searchFilter;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (includeEmpty) params.include_empty = true;
            const { data } = await supplierReconciliationApi.listRows(params);
            setRows(data.rows || []);
            setCounts(data.counts || {});
            setEmptyPosHidden(data.empty_pos_hidden || 0);
            setTolerance(data.tolerance || tolerance);
        } catch (err) {
            toast.error('Failed to load reconciliation data');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, supplierFilter, searchFilter, dateFrom, dateTo, includeEmpty]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        fetchRows();
    }, [fetchRows]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const handleSync = async () => {
        setSyncing(true);
        try {
            const { data } = await supplierReconciliationApi.syncFromEconomic(365);
            toast.success(
                `Synced: ${data.fetched} fetched · ${data.inserted} new · ${data.updated} updated · ${data.cleaned_duplicates || 0} dedup'd · ${data.auto_matched} auto-matched · ${data.suggested} suggested`
            );
            await fetchRows();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            toast.error(`Sync failed: ${msg}`);
        } finally {
            setSyncing(false);
        }
    };

    const handleUnmatch = async (matchId) => {
        try {
            await supplierReconciliationApi.unmatch(matchId);
            toast.success('Unmatched');
            await fetchRows();
        } catch (err) {
            toast.error('Could not unmatch');
        }
    };

    const summaryTiles = useMemo(() => [
        { label: 'Matched', key: 'matched', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
        { label: 'Variance', key: 'variance', icon: AlertTriangle, color: 'text-amber-600 bg-amber-50' },
        { label: 'Needs review', key: 'variance_review', icon: HelpCircle, color: 'text-orange-600 bg-orange-50' },
        { label: 'Suggested', key: 'suggested', icon: LinkIcon, color: 'text-blue-600 bg-blue-50' },
        { label: 'Orphan invoices', key: 'unmatched_invoice', icon: FileText, color: 'text-slate-600 bg-slate-50' },
    ], []);

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1400px] mx-auto px-6 py-8">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                            <DollarSign className="h-4 w-4" />
                            <span>Finance</span>
                        </div>
                        <h1 className="text-3xl font-display font-semibold text-slate-900">
                            Supplier Reconciliation
                        </h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                            Match FLOW purchase orders against actual supplier invoices imported from e-conomic.
                            Tolerance: ±{tolerance.flat} or {(tolerance.pct * 100).toFixed(1)}% (same currency only).
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isSuperAdmin && (
                            <Button
                                variant="outline"
                                onClick={() => window.location.href = '/finance/reconciliation/diagnostic'}
                                data-testid="diagnostic-btn"
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                Inspect raw payload
                            </Button>
                        )}
                        {isSuperAdmin && (
                            <Button
                                onClick={handleSync}
                                disabled={syncing}
                                data-testid="sync-economic-btn"
                            >
                                {syncing ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing…</>
                                ) : (
                                    <><RefreshCw className="h-4 w-4 mr-2" />Sync from e-conomic</>
                                )}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    {summaryTiles.map((tile) => {
                        const Icon = tile.icon;
                        const active = statusFilter === tile.key;
                        return (
                            <button
                                key={tile.key}
                                onClick={() => setStatusFilter(active ? 'all' : tile.key)}
                                className={`text-left bg-card border rounded-lg p-4 transition-all hover:border-slate-300 ${active ? 'border-slate-900 ring-2 ring-slate-900/10' : 'border-slate-200'}`}
                                data-testid={`tile-${tile.key}`}
                            >
                                <div className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${tile.color} mb-2`}>
                                    <Icon className="h-4 w-4" />
                                </div>
                                <div className="text-2xl font-display font-semibold text-slate-900">
                                    {counts[tile.key] || 0}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">{tile.label}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Filters */}
                <Card className="mb-4 bg-card">
                    <CardContent className="p-4 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                                <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger data-testid="filter-status">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600 mb-1 block">Supplier</label>
                                <Input
                                    value={supplierFilter}
                                    onChange={(e) => setSupplierFilter(e.target.value)}
                                    placeholder="e.g. Sony"
                                    data-testid="filter-supplier"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600 mb-1 block">Search</label>
                                <Input
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    placeholder="PO, cat#, invoice no., order…"
                                    data-testid="filter-search"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600 mb-1 block">From</label>
                                <Input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    data-testid="filter-date-from"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600 mb-1 block">To</label>
                                <Input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    data-testid="filter-date-to"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="include-empty-pos"
                                    checked={includeEmpty}
                                    onCheckedChange={setIncludeEmpty}
                                    data-testid="toggle-include-empty"
                                />
                                <Label
                                    htmlFor="include-empty-pos"
                                    className="text-xs text-slate-600 cursor-pointer select-none"
                                >
                                    Vis tomme POs
                                    {!includeEmpty && emptyPosHidden > 0 && (
                                        <span
                                            className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600"
                                            data-testid="empty-pos-hidden-count"
                                        >
                                            {emptyPosHidden} skjult
                                        </span>
                                    )}
                                </Label>
                            </div>
                            <p className="text-[11px] text-slate-400">
                                Tomme placeholder POs (beløb = 0, uden cat# og ordre) skjules som standard.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="bg-card">
                    <CardHeader className="border-b border-slate-100 py-3">
                        <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <Filter className="h-4 w-4" />
                            {rows.length} row{rows.length === 1 ? '' : 's'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="py-16 flex justify-center"><LoadingSpinner /></div>
                        ) : rows.length === 0 ? (
                            <div className="py-16 text-center text-slate-400">
                                <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                <div className="text-sm">No rows match the current filters</div>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                        <tr>
                                            <th className="text-left py-2 px-3 font-medium">PO</th>
                                            <th className="text-left py-2 px-3 font-medium">Cat# / Order</th>
                                            <th className="text-left py-2 px-3 font-medium">Supplier Invoice</th>
                                            <th className="text-right py-2 px-3 font-medium">Expected</th>
                                            <th className="text-right py-2 px-3 font-medium">Actual</th>
                                            <th className="text-right py-2 px-3 font-medium">Δ</th>
                                            <th className="text-left py-2 px-3 font-medium">Status</th>
                                            <th className="text-left py-2 px-3 font-medium w-32">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, idx) => (
                                            <ReconciliationRow
                                                key={`${row.row_kind}-${(row.purchase || {}).id || (row.invoice || {}).id || idx}`}
                                                row={row}
                                                onMatch={(r) => setMatchRow(r)}
                                                onUnmatch={handleUnmatch}
                                                onExplain={(inv) => setExplainInvoice(inv)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>

            <ManualMatchDialog
                row={matchRow}
                open={!!matchRow}
                onClose={() => setMatchRow(null)}
                onConfirm={fetchRows}
            />

            <ExplainDialog
                invoice={explainInvoice}
                open={!!explainInvoice}
                onClose={() => setExplainInvoice(null)}
            />
        </div>
    );
}
